import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Avatar from './Avatar';
import { friendshipApi } from '../services/friendshipApi';
import { postApi } from '../services/postApi';
import './RightSidebar.css';

// Extract hashtags from text
const extractHashtags = (text) => {
  if (!text) return [];
  const regex = /#(\w+)/g;
  const matches = text.match(regex);
  return matches ? matches.map(tag => tag.slice(1).toLowerCase()) : [];
};

// Count and sort hashtags from posts
const getTrendingFromPosts = (posts, limit = 5) => {
  const tagCounts = {};
  posts.forEach(post => {
    const tags = extractHashtags(post.content);
    tags.forEach(tag => {
      tagCounts[tag] = (tagCounts[tag] || 0) + 1;
    });
  });
  return Object.entries(tagCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([tag, count]) => ({ tag, postCount: count }));
};

// Follow Suggestion Item
const FollowSuggestionItem = ({ user, onSuccess }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [status, setStatus] = useState('none'); // none | pending | friends
  const [loading, setLoading] = useState(false);

  const handleSendRequest = async (e) => {
    e.stopPropagation();
    if (status !== 'none' || loading) return;

    setLoading(true);
    try {
      await friendshipApi.sendFriendRequest(user.id);
      setStatus('pending');
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('Failed to send friend request:', err);
    } finally {
      setLoading(false);
    }
  };

  const getButtonText = () => {
    if (status === 'pending') return t('friends.sent') || 'Đã gửi';
    if (status === 'friends') return t('friends.friends') || 'Bạn bè';
    return t('friends.add') || 'Thêm bạn';
  };

  const getButtonClass = () => {
    let cls = 'follow-btn';
    if (status === 'pending') cls += ' pending';
    if (status === 'friends') cls += ' friends';
    return cls;
  };

  return (
    <div className="follow-item" onClick={() => navigate(`/profile/${user.username}`)}>
      <Avatar user={user} size="small" />
      <div className="follow-item-info">
        <div className="follow-item-name">{user.display_name || user.displayName}</div>
        <div className="follow-item-username">@{user.username}</div>
      </div>
      <button
        className={getButtonClass()}
        onClick={handleSendRequest}
        disabled={status === 'friends' || loading}
      >
        {getButtonText()}
      </button>
    </div>
  );
};

// Trending Item
const TrendingItem = ({ topic, onClick }) => {
  return (
    <div className="trending-item" onClick={() => onClick(topic.tag)}>
      <div className="trending-icon">#</div>
      <div className="trending-item-info">
        <div className="trending-item-name">{topic.tag}</div>
        <div className="trending-item-meta">{topic.postCount} posts</div>
      </div>
    </div>
  );
};

// Online User Item
const OnlineUserItem = ({ user, onClick }) => {
  return (
    <div className="online-user-item" onClick={() => onClick(user)}>
      <div className="online-dot" />
      <Avatar user={user} size="small" />
      <div className="online-user-info">
        <div className="online-user-name">{user.display_name || user.displayName}</div>
        <div className="online-user-subtitle">@{user.username}</div>
      </div>
    </div>
  );
};

// Right Sidebar Component
const RightSidebar = ({ onlineUsers = [], onOpenChat }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [suggestions, setSuggestions] = useState([]);
  const [friends, setFriends] = useState([]);
  const [trending, setTrending] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch friend suggestions, friends, and trending topics
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch all data in parallel
        const [suggestionsRes, friendsRes, postsRes] = await Promise.all([
          friendshipApi.getSuggestions(5),
          friendshipApi.getFriends(),
          postApi.getFeed(50, 0), // Fetch recent posts for trending
        ]);

        if (suggestionsRes.data?.suggestions) {
          setSuggestions(suggestionsRes.data.suggestions);
        }

        if (friendsRes.data?.friends) {
          setFriends(friendsRes.data.friends);
        }

        // Extract trending from posts
        if (postsRes.data) {
          const trendingTopics = getTrendingFromPosts(postsRes.data);
          setTrending(trendingTopics);
        }
      } catch (err) {
        console.error('Failed to load sidebar data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Filter friends who are online
  const onlineFriends = friends.filter(friend => onlineUsers.has(friend.id));

  const handleTrendingClick = (tag) => {
    navigate(`/search?q=${encodeURIComponent(tag)}`);
  };

  const handleOnlineUserClick = (user) => {
    if (onOpenChat) {
      onOpenChat(user);
    }
  };

  const handleSuggestionSuccess = () => {
    // Refresh suggestions or just move the item
  };

  return (
    <aside className="right-sidebar">
      {/* Who to Follow Section */}
      <section className="sidebar-section">
        <div className="sidebar-section-header">
          <h3 className="sidebar-section-title">{t('friends.suggestions') || 'Gợi ý cho bạn'}</h3>
        </div>
        <div className="sidebar-section-content">
          {loading ? (
            <div className="sidebar-loading">
              <div className="loading-spinner" style={{ width: 20, height: 20 }} />
            </div>
          ) : suggestions.length > 0 ? (
            suggestions.map((user) => (
              <FollowSuggestionItem
                key={user.id}
                user={user}
                onSuccess={handleSuggestionSuccess}
              />
            ))
          ) : (
            <div className="sidebar-empty">
              {t('friends.noSuggestions') || 'Không có gợi ý nào'}
            </div>
          )}
        </div>
        {suggestions.length > 0 && (
          <a className="show-more-link" href="/friends?tab=suggestions">
            {t('common.showMore') || 'Xem thêm'}
          </a>
        )}
      </section>

      {/* Trending Topics Section */}
      <section className="sidebar-section">
        <div className="sidebar-section-header">
          <h3 className="sidebar-section-title">{t('sidebar.trending') || 'Xu hướng'}</h3>
        </div>
        <div className="sidebar-section-content">
          {trending.length > 0 ? (
            trending.map((topic, index) => (
              <TrendingItem
                key={`${topic.tag}-${index}`}
                topic={topic}
                onClick={handleTrendingClick}
              />
            ))
          ) : (
            <div className="sidebar-empty">
              {t('sidebar.noTrending') || 'No trending topics'}
            </div>
          )}
        </div>
      </section>

      {/* Online Users Section */}
      <section className="sidebar-section">
        <div className="sidebar-section-header">
          <h3 className="sidebar-section-title">{t('sidebar.online') || 'Đang hoạt động'}</h3>
        </div>
        <div className="sidebar-section-content">
          {onlineFriends.length > 0 ? (
            onlineFriends.slice(0, 6).map((user) => (
              <OnlineUserItem
                key={user.id}
                user={user}
                onClick={handleOnlineUserClick}
              />
            ))
          ) : (
            <div className="sidebar-empty">
              {t('sidebar.noOnline') || 'Không có ai đang hoạt động'}
            </div>
          )}
        </div>
      </section>
    </aside>
  );
};

export default RightSidebar;